import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export type OperationType = 'PURCHASE' | 'PROCESSING' | 'COMMISSION' | 'IMPORT';

export interface GoodsReceiptItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  series_id?: string | null;
  characteristic_id?: string | null;
  /** ч.662: склад рядка (мультисклад; NULL = «Склад за замовчуванням»). */
  warehouse_id?: string | null;
  quantity: string;
  uom_id?: string | null;
  price: string;
  amount_without_vat: string;
  discount_percent?: string;
  discount_amount?: string;
  vat_rate_id: string;
  vat_amount: string;
  total_amount: string;
  project_id?: string | null;
  /** ПДВ-призначення (для класифікації ВК — ПКУ ст.199). */
  tax_purpose_id?: string | null;
  /** Перебивка рахунку запасів рядка (вкладка «Рахунки обліку», 2026-07-31).
   * Порожньо — авто: правило/картка/281. Читається постингом. */
  account_code?: string | null;
}

export interface GoodsReceiptListItem {
  currency_code?: string | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  warehouse_id: string | null;  // ч.912: null — документ лише послуг
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3b:
  state?: CancellableState;
  // 2026-08-04: контроль вхідних ПН по приходах (тільки платники ПДВ).
  pn_id?: string | null;
  pn_number?: string | null;
  pn_is_registered?: boolean | null;
  pn_external_source?: string | null;
  pn_registration_date?: string | null;
  contractor_is_vat_payer?: boolean;
}

/** Рядок ТЧ «Послуги» (2026-07-29): Дт cost_account_code — Кт 631. */
export interface GoodsReceiptService {
  id?: string;
  line_number: number;
  nomenclature_id?: string | null;
  description?: string | null;
  quantity?: string | null;
  price?: string | null;
  amount_without_vat: string;
  vat_rate_id?: string | null;
  vat_amount: string;
  total_amount: string;
  cost_account_code?: string | null;
  /** Стаття затрат (субконто Дт; авто з картки номенклатури), 2026-08-12. */
  cost_item_id?: string | null;
  /** Призначення ПДВ послуги (ст.199 ПКУ), 2026-07-31. */
  tax_purpose_id?: string | null;
}

export interface GoodsReceipt extends GoodsReceiptListItem {
  currency_code?: string | null;
  exchange_rate?: string | null;
  operation_type: OperationType;
  contract_id: string;
  supplier_order_id: string | null;
  advance_offset_mode?: 'AUTO' | 'NONE';
  supplier_doc_number: string | null;
  supplier_doc_date: string | null;
  prices_include_vat: boolean;
  // ч.1374: «Враховувати ПДВ» / «Авторозрахунок ПДВ» (1С).
  vat_considered: boolean;
  vat_auto: boolean;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: GoodsReceiptItem[];
  services?: GoodsReceiptService[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GoodsReceiptCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_type?: OperationType;
  contractor_id: string;
  /** 2026-06-02: nullable — backend auto-create default contract. */
  contract_id: string | null;
  warehouse_id: string | null;  // ч.912: null — документ лише послуг
  supplier_order_id?: string | null;
  advance_offset_mode?: 'AUTO' | 'NONE';
  supplier_doc_number?: string | null;
  supplier_doc_date?: string | null;
  /** 2026-08-09: курс валютного документа (наш док — з НБУ кнопкою). */
  exchange_rate?: string | null;
  prices_include_vat?: boolean;
  vat_considered?: boolean;
  vat_auto?: boolean;
  comment?: string | null;
  items: GoodsReceiptItem[];
  services?: GoodsReceiptService[];
  project_id?: string | null;
}

export type GoodsReceiptUpdate = Partial<GoodsReceiptCreate>;

export const goodsReceipts = {
  list: (params?: { organization_id?: string; contractor_id?: string; nomenclature_id?: string; posted?: boolean; limit?: number; offset?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'
    date_from?: string;
    date_to?: string;
    pn_link?: 'with' | 'without'; }) =>
    api.get<Page<GoodsReceiptListItem>>('/goods-receipts', { params }).then((r) => r.data),
  get: (id: string) => api.get<GoodsReceipt>(`/goods-receipts/${id}`).then((r) => r.data),
  // 2026-07-31: реальні рахунки запасів, які підбере проведення
  // (вкладка «Рахунки обліку», Закон #6 — той самий резолвер).
  previewAccounts: (p: {
    organization_id: string;
    items: Array<{ nomenclature_id: string; warehouse_id?: string | null }>;
  }) => api.post<Array<{ nomenclature_id: string; account_code: string | null }>>(
    '/goods-receipts/preview-accounts', p).then((r) => r.data),
  create: (payload: GoodsReceiptCreate) =>
    api.post<GoodsReceipt>('/goods-receipts', payload).then((r) => r.data),
  update: (id: string, payload: GoodsReceiptUpdate) =>
    api.patch<GoodsReceipt>(`/goods-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/goods-receipts/${id}`).then(() => undefined),
  post: (id: string) => api.post<GoodsReceipt>(`/goods-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<GoodsReceipt>(`/goods-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

/** Курс НБУ на дату (2026-08-09): наш валютний документ — кнопкою. */
export const currencyRate = {
  get: (code: string, onDate: string) =>
    api.get<{ code: string; date: string; rate: string; source: string }>(
      '/refs/currency-rate', { params: { code, on_date: onDate } })
      .then((r) => r.data),
};
